import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  FileText,
  FileCode,
  FileType,
  File,
  CheckCircle,
  AlertCircle,
  Loader,
} from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import api, { Datasource, IngestionResult } from '../../../services/api';
import styles from './DocumentUploadModal.module.css';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  assistantId: string;
  existingDatasource?: Datasource | null;
  onUploadComplete: () => void;
}

type UploadStatus = 'idle' | 'creating' | 'uploading' | 'processing' | 'complete' | 'error';

interface UploadState {
  status: UploadStatus;
  progress: number;
  message: string;
  result?: IngestionResult;
  error?: string;
}

const SUPPORTED_EXTENSIONS = [
  '.pdf', '.docx', '.doc', '.md', '.markdown', '.mdx',
  '.txt', '.ts', '.tsx', '.js', '.jsx', '.py', '.java',
  '.go', '.rs', '.rb', '.c', '.cpp', '.h', '.cs', '.php',
  '.yaml', '.yml', '.json', '.xml', '.html', '.css', '.sql',
];

const getFileIcon = (fileName: string) => {
  const ext = fileName.toLowerCase().split('.').pop();
  if (ext === 'pdf') return <FileText size={24} className={styles.iconPdf} />;
  if (['ts', 'tsx', 'js', 'jsx', 'py', 'java', 'go', 'rs', 'rb', 'c', 'cpp', 'cs', 'php'].includes(ext || '')) {
    return <FileCode size={24} className={styles.iconCode} />;
  }
  if (['md', 'markdown', 'mdx'].includes(ext || '')) {
    return <FileType size={24} className={styles.iconMarkdown} />;
  }
  return <File size={24} className={styles.iconDefault} />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  assistantId,
  existingDatasource,
  onUploadComplete,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [datasourceName, setDatasourceName] = useState('');
  const [datasourceDescription, setDatasourceDescription] = useState('');
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0,
    message: '',
  });
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setDatasourceName('');
    setDatasourceDescription('');
    setUploadState({ status: 'idle', progress: 0, message: '' });
    setIsDragging(false);
  }, []);

  const handleClose = () => {
    if (uploadState.status !== 'uploading' && uploadState.status !== 'processing') {
      resetState();
      onClose();
    }
  };

  const handleFileSelect = (file: File) => {
    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      setUploadState({
        status: 'error',
        progress: 0,
        message: `Unsupported file type: ${ext}`,
        error: `Supported types: ${SUPPORTED_EXTENSIONS.join(', ')}`,
      });
      return;
    }
    setSelectedFile(file);
    if (!datasourceName) {
      setDatasourceName(file.name.split('.').slice(0, -1).join('.'));
    }
    setUploadState({ status: 'idle', progress: 0, message: '' });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    let datasourceId = existingDatasource?.id;

    try {
      // Create datasource if needed
      if (!datasourceId) {
        setUploadState({
          status: 'creating',
          progress: 0,
          message: 'Creating datasource...',
        });

        const dsResponse = await api.createDatasource(assistantId, {
          name: datasourceName || selectedFile.name,
          description: datasourceDescription,
          type: 'file',
        });
        datasourceId = dsResponse.data.id;
      }

      // Upload file
      setUploadState({
        status: 'uploading',
        progress: 0,
        message: 'Uploading document...',
      });

      const result = await api.uploadDocument(
        assistantId,
        datasourceId,
        selectedFile,
        (progress) => {
          setUploadState((prev) => ({
            ...prev,
            progress,
            message: progress < 100 ? 'Uploading document...' : 'Processing in background...',
          }));
        }
      );

      if (result.data.error) {
        setUploadState({
          status: 'error',
          progress: 0,
          message: 'Upload failed',
          error: result.data.error,
        });
      } else {
        // Handle async response - upload accepted for background processing
        const isAsync = result.data.status === 'processing' || result.data.jobId;
        setUploadState({
          status: 'complete',
          progress: 100,
          message: isAsync 
            ? 'Document uploaded! Processing in background...' 
            : 'Document uploaded and processed!',
          result: result.data,
        });
      }
    } catch (error) {
      setUploadState({
        status: 'error',
        progress: 0,
        message: 'Upload failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const handleDone = () => {
    resetState();
    onUploadComplete();
  };

  const renderContent = () => {
    switch (uploadState.status) {
      case 'idle':
        return (
          <>
            {/* File Drop Zone */}
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dragging : ''} ${selectedFile ? styles.hasFile : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={SUPPORTED_EXTENSIONS.join(',')}
                onChange={handleFileInputChange}
                className={styles.fileInput}
              />
              
              {selectedFile ? (
                <div className={styles.selectedFile}>
                  {getFileIcon(selectedFile.name)}
                  <div className={styles.fileInfo}>
                    <span className={styles.fileName}>{selectedFile.name}</span>
                    <span className={styles.fileSize}>{formatFileSize(selectedFile.size)}</span>
                  </div>
                  <button
                    className={styles.removeFile}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={32} className={styles.uploadIcon} />
                  <p className={styles.dropText}>
                    Drag and drop your document here, or click to browse
                  </p>
                  <p className={styles.supportedTypes}>
                    Supports: PDF, DOCX, Markdown, Code files
                  </p>
                </>
              )}
            </div>

            {/* Datasource Details */}
            {!existingDatasource && selectedFile && (
              <div className={styles.formFields}>
                <Input
                  label="Datasource Name"
                  value={datasourceName}
                  onChange={(e) => setDatasourceName(e.target.value)}
                  placeholder="Enter a name for this datasource"
                />
                <Input
                  label="Description (optional)"
                  value={datasourceDescription}
                  onChange={(e) => setDatasourceDescription(e.target.value)}
                  placeholder="Brief description of this document"
                />
              </div>
            )}
          </>
        );

      case 'creating':
      case 'uploading':
      case 'processing':
        return (
          <div className={styles.progressContainer}>
            <Loader size={48} className={styles.spin} />
            <p className={styles.progressMessage}>{uploadState.message}</p>
            {uploadState.status === 'uploading' && (
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${uploadState.progress}%` }}
                />
              </div>
            )}
          </div>
        );

      case 'complete':
        return (
          <div className={styles.resultContainer}>
            <CheckCircle size={48} className={styles.successIcon} />
            <h3>Upload Complete!</h3>
            {uploadState.result && (
              <div className={styles.resultStats}>
                <div className={styles.resultStat}>
                  <span className={styles.statValue}>{uploadState.result.totalChunks}</span>
                  <span className={styles.statLabel}>Chunks Created</span>
                </div>
                <div className={styles.resultStat}>
                  <span className={styles.statValue}>{uploadState.result.totalTokens.toLocaleString()}</span>
                  <span className={styles.statLabel}>Tokens Processed</span>
                </div>
                <div className={styles.resultStat}>
                  <span className={styles.statValue}>{(uploadState.result.processingTime / 1000).toFixed(1)}s</span>
                  <span className={styles.statLabel}>Processing Time</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'error':
        return (
          <div className={styles.errorContainer}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h3>{uploadState.message}</h3>
            <p className={styles.errorText}>{uploadState.error}</p>
          </div>
        );
    }
  };

  const renderFooter = () => {
    switch (uploadState.status) {
      case 'idle':
        return (
          <>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile}
            >
              <Upload size={16} />
              Upload Document
            </Button>
          </>
        );

      case 'creating':
      case 'uploading':
      case 'processing':
        return null;

      case 'complete':
        return (
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        );

      case 'error':
        return (
          <>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={resetState}>
              Try Again
            </Button>
          </>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={existingDatasource ? `Add Document to ${existingDatasource.name}` : 'Upload Document'}
      footer={<div className={styles.footer}>{renderFooter()}</div>}
      size="md"
    >
      <div className={styles.content}>{renderContent()}</div>
    </Modal>
  );
};

export default DocumentUploadModal;
